/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  computed,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  of,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs";

// PrimeNG imports
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { TagModule } from "primeng/tag";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Our services
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import {
  PagedResult,
  QuickLinkListItem,
  QuickLinkActionType,
  QuickLinkStatus,
  PageEvent,
  SortEvent,
  UsageLimitType,
} from "../../interfaces/quick-link-list-item.interface";
import {
  DEFAULT_FILTER_VALUES,
  IQuickLinksFilterControls,
  QuickLinksFilter,
} from "../../interfaces/quick-links-filter.interface";
import { QuickLinksService } from "../../services/quick-links.service";
import { QuickLinksFiltersComponent } from "../../components/quick-links-filters/quick-links-filters.component";
import { QuickLinksTableComponent } from "../../components/quick-links-table/quick-links-table.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";
// Static import for dialog - required for embedded mode where dynamic imports fail
// due to chunk loading from wrong origin
import { QuickLinkMetricsDialogComponent } from "../../components/quick-link-metrics-dialog/quick-link-metrics-dialog.component";

/**
 * Quick Links Wrapper Component
 * Main orchestrating component that manages filters, table, and operations
 * Follows the same pattern as scheduled-orders-list
 */
@Component({
  selector: "app-quick-links-wrapper",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    QuickLinksFiltersComponent,
    QuickLinksTableComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./quick-links-wrapper.component.html",
  styleUrl: "./quick-links-wrapper.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, DialogService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class QuickLinksWrapperComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode (web component / iframe)
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;
  @Input() hideToolbar: boolean = false;

  // Reactive state using signals
  readonly currentSiteId = signal<number | null>(null);

  // Data signals
  private readonly quickLinks = signal<PagedResult<QuickLinkListItem> | null>(
    null,
  );

  // UI state - will be initialized based on screen size
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false; // Track if user manually toggled sidebar

  // Loading states
  private readonly isLoading = signal(false);
  private readonly isInitialLoading = signal(true);

  // Form management
  filterForm: FormGroup<IQuickLinksFilterControls>;

  // Computed values
  readonly data = computed(() => this.quickLinks());
  readonly loading = computed(() => this.isLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());
  readonly hasActiveFilters = computed(() => {
    const form = this.filterForm;
    if (!form) return false;

    const values = form.value;

    // Return true if ANY filter is selected (including status)
    // This ensures the Clear All button is enabled when filters are visible
    // Note: usageLimitType filter is commented out for now
    return !!(
      values.search?.trim() ||
      values.statuses?.length ||
      values.actionTypes?.length
    );
  });

  // Component lifecycle
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly elementRef: ElementRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly quickLinksService: QuickLinksService,
    private readonly sessionService: SessionService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly dialogService: DialogService,
    private readonly fb: FormBuilder,
  ) {
    // Initialize form in constructor
    // Default status filter: show Enabled (1) and Disabled (0), hide Deleted (2)
    this.filterForm = this.fb.group<IQuickLinksFilterControls>({
      siteId: this.fb.nonNullable.control(0),
      search: this.fb.control(null),
      statuses: this.fb.nonNullable.control([0, 1] as QuickLinkStatus[]),
      actionTypes: this.fb.nonNullable.control([]),
      usageLimitType: this.fb.nonNullable.control([]),
      page: this.fb.nonNullable.control(DEFAULT_FILTER_VALUES.page),
      pageSize: this.fb.nonNullable.control(DEFAULT_FILTER_VALUES.pageSize),
      sortBy: this.fb.control(DEFAULT_FILTER_VALUES.sortBy),
      sortDirection: this.fb.nonNullable.control(
        DEFAULT_FILTER_VALUES.sortDirection,
      ),
    });
  }

  ngOnInit(): void {
    // Detect embedded mode from HTML attributes (for web components)
    this.detectEmbeddedModeFromAttributes();

    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
    this.initializeComponent();
    this.loadPersistedFiltersToForm();
    this.setupDataSubscriptions();
    this.loadInitialData();
  }

  /**
   * Detect embedded mode from HTML attributes
   * Required for web component mode where @Input bindings don't work automatically
   */
  private detectEmbeddedModeFromAttributes(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check embedded-mode attribute
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.inputSiteId = parseInt(siteIdString, 10);
    }

    // Get token from attribute and convert to QPilotAccessToken
    // Angular Elements may bind the attribute as a raw string, so we check for proper structure
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Get hideToolbar from attribute
    const hideToolbarAttr =
      element.getAttribute("hide-toolbar") ??
      element.getAttribute("hideToolbar");
    if (hideToolbarAttr === "true") {
      this.hideToolbar = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize component setup
   */
  private initializeComponent(): void {
    if (this.embeddedMode && this.inputSiteId) {
      // Embedded mode: use inputSiteId
      this.currentSiteId.set(this.inputSiteId);
      this.filterForm.patchValue({ siteId: this.inputSiteId });
    } else {
      // Standard mode: get site ID from parent route parameter
      const siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
      if (siteId && siteId > 0) {
        this.currentSiteId.set(siteId);
        this.filterForm.patchValue({ siteId });
      } else {
        console.warn("No siteId found in route parameters");
      }
    }
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  getAccessToken(): QPilotAccessToken | undefined {
    // In embedded mode, use inputToken
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    // In standard mode, use session service (convert null to undefined)
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Setup reactive data subscriptions
   */
  private setupDataSubscriptions(): void {
    let isFirstLoad = true;

    // Watch for filter changes and load data
    this.filterForm.valueChanges
      .pipe(
        startWith(this.filterForm.value),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((formValue) => {
          if (!formValue.siteId) {
            return of({ quickLinks: null });
          }

          const accessToken = this.getAccessToken();
          if (!accessToken) {
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: "No access token available. Please login again.",
              life: 5000,
            });
            return of({ quickLinks: null });
          }

          // Set loading states
          if (isFirstLoad) {
            this.isInitialLoading.set(true);
          }
          this.isLoading.set(true);

          const filter = this.buildFilterFromForm(formValue);

          // Load quick links
          return combineLatest({
            quickLinks: this.quickLinksService.getQuickLinks(
              filter,
              accessToken,
            ),
          }).pipe(
            catchError((error) => {
              console.error("Error loading quick links:", error);
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to load quick links. Please try again.",
                life: 5000,
              });
              return of({ quickLinks: null });
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result: any) => {
          const { quickLinks } = result;
          this.quickLinks.set(quickLinks);
          this.isLoading.set(false);

          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Subscription error:", error);
          this.isLoading.set(false);

          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }

          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    // Trigger initial load by marking form as touched
    this.filterForm.markAsDirty();
    this.filterForm.updateValueAndValidity();
  }

  /**
   * Load persisted filters from localStorage into the form
   */
  private loadPersistedFiltersToForm(): void {
    try {
      const saved = localStorage.getItem("quickLinks.filters");
      if (saved) {
        const filters = JSON.parse(saved);

        // Restore filter-specific values, not pagination/sorting
        // Default status: show Enabled (1) and Disabled (0), hide Deleted (2)
        this.filterForm.patchValue(
          {
            search: filters.search,
            statuses: filters.statuses || ([0, 1] as QuickLinkStatus[]),
            actionTypes: filters.actionTypes || [],
            usageLimitType: filters.usageLimitType || [],
          },
          { emitEvent: false },
        );
      }
    } catch (error) {
      console.warn("Failed to load persisted filters:", error);
    }
  }

  /**
   * Build API filter from form values
   */
  private buildFilterFromForm(
    formValue: Partial<{
      siteId: number;
      search: string | null;
      statuses: QuickLinkStatus[];
      actionTypes: QuickLinkActionType[];
      usageLimitType: UsageLimitType[];
      page: number;
      pageSize: number;
      sortBy: string | null;
      sortDirection: "asc" | "desc";
    }>,
  ): QuickLinksFilter {
    const filter: QuickLinksFilter = {
      siteId: formValue.siteId || 0,
      search: formValue.search?.trim() || undefined,
      statuses:
        formValue.statuses && formValue.statuses.length > 0
          ? formValue.statuses
          : undefined,
      actionTypes:
        formValue.actionTypes && formValue.actionTypes.length > 0
          ? formValue.actionTypes
          : undefined,
      usageLimitType:
        formValue.usageLimitType && formValue.usageLimitType.length > 0
          ? formValue.usageLimitType
          : undefined,
      pageSize: formValue.pageSize || DEFAULT_FILTER_VALUES.pageSize,
      page: formValue.page || DEFAULT_FILTER_VALUES.page,
      orderBy: formValue.sortBy || undefined,
      order: formValue.sortDirection === "desc" ? "desc" : "asc",
    };

    return filter;
  }

  /**
   * Handle filter changes from child component
   */
  onFiltersChanged(filter: QuickLinksFilter): void {
    // This is handled automatically by the form valueChanges subscription
    // Reset to first page when filters change
    this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
  }

  /**
   * Handle clear filters from child component
   * Clears ALL filters including status (sets to empty array)
   * Defaults are only applied on initial page load, not when clearing
   */
  onClearFilters(): void {
    // Reset form but keep siteId and pagination defaults
    // Clear ALL filters including status - defaults only apply on page load
    this.filterForm.patchValue({
      search: null,
      statuses: [] as QuickLinkStatus[],
      actionTypes: [],
      usageLimitType: [],
      page: DEFAULT_FILTER_VALUES.page,
      pageSize: DEFAULT_FILTER_VALUES.pageSize,
      sortBy: DEFAULT_FILTER_VALUES.sortBy,
      sortDirection: DEFAULT_FILTER_VALUES.sortDirection,
    });

    // Clear persisted filters
    localStorage.removeItem("quickLinks.filters");
  }

  /**
   * Handle table sorting
   */
  onSortChanged(event: SortEvent): void {
    this.filterForm.patchValue({
      sortBy: event.field,
      sortDirection: event.order === 1 ? "asc" : "desc",
      page: 1, // Reset to first page
    });
  }

  /**
   * Handle table pagination
   */
  onPageChanged(event: PageEvent): void {
    // Calculate page number from first index if page is undefined
    const currentPage =
      event.page !== undefined
        ? event.page
        : Math.floor(event.first / event.rows);

    this.filterForm.patchValue({
      page: currentPage + 1, // PrimeNG uses 0-based pages, API uses 1-based
      pageSize: event.rows,
    });
  }

  /**
   * Handle individual row actions
   */
  onRowAction(event: { action: string; item: QuickLinkListItem }): void {
    const { action, item } = event;

    switch (action) {
      case "edit":
        this.editQuickLink(item);
        break;
      case "viewMetrics":
        this.viewMetrics(item);
        break;
      // COMMENTED OUT: API not ready yet
      // case "generateToken":
      //   this.generateToken(item);
      //   break;
      case "delete":
        this.deleteQuickLink(item);
        break;
      case "permanentDelete":
        this.permanentDeleteQuickLink(item);
        break;
      case "statusToggle":
        this.toggleStatus(item);
        break;
      case "oneTimeUseToggle":
        this.toggleOneTimeUse(item);
        break;
      default:
        console.warn("Unknown row action:", action);
    }
  }

  /**
   * Navigate to create page
   */
  openCreateDialog(): void {
    const siteId = this.currentSiteId();
    if (!siteId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No site selected",
        life: 5000,
      });
      return;
    }

    if (this.embeddedMode) {
      // Emit event for parent application to handle
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "quick-links-list",
          action: "create",
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Navigate to create page
      void this.router.navigate([
        "/widgets/dashboard",
        siteId,
        "quick-actions",
        "create",
      ]);
    }
  }

  /**
   * Navigate to edit page
   */
  private editQuickLink(item: QuickLinkListItem): void {
    const siteId = this.currentSiteId();
    if (!siteId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No site selected",
        life: 5000,
      });
      return;
    }

    if (this.embeddedMode) {
      // Emit event for parent application to handle
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "quick-links-list",
          action: "edit",
          quickLinkId: item.id,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Navigate to edit page
      void this.router.navigate([
        "/widgets/dashboard",
        siteId,
        "quick-actions",
        "edit",
        item.id,
      ]);
    }
  }

  /**
   * View metrics
   * Uses static import to avoid chunk loading issues in embedded mode
   */
  private viewMetrics(item: QuickLinkListItem): void {
    const siteId = this.currentSiteId();
    if (!siteId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No site selected",
      });
      return;
    }

    // Use static import to avoid chunk loading issues in embedded mode
    this.dialogService.open(QuickLinkMetricsDialogComponent, {
      header: "Quick Link Metrics",
      width: "900px",
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      focusOnShow: false, // Workaround for PrimeNG v19 bug #17479
      breakpoints: {
        "960px": "90vw",
        "640px": "95vw",
      },
      data: {
        quickLinkId: item.id,
        quickLinkName: item.name,
        siteId: siteId,
        embeddedMode: this.embeddedMode, // Pass embedded mode for API calls
        accessToken: this.getAccessToken(), // Pass access token for embedded mode
      },
    });

    // No need to refresh data after viewing metrics
  }

  // COMMENTED OUT: API not ready yet
  /**
   * Generate token for single-use link
   */
  // private generateToken(item: QuickLinkListItem): void {
  //   console.log("Generate token for:", item.id);
  //
  //   const siteId = this.currentSiteId();
  //   if (!siteId) {
  //     this.messageService.add({
  //       severity: "error",
  //       summary: "Error",
  //       detail: "No site selected",
  //     });
  //     return;
  //   }
  //
  //   // Dynamically import the component
  //   void import(
  //     "../../components/generate-token-dialog/generate-token-dialog.component"
  //   ).then((m) => {
  //     const ref = this.dialogService.open(m.GenerateTokenDialogComponent, {
  //       header: "Generate Single-Use Token",
  //       width: "650px",
  //       modal: true,
  //       dismissableMask: false,
  //       data: {
  //         quickLinkId: item.id,
  //         quickLinkName: item.name,
  //         siteId: siteId,
  //       },
  //     });
  //   });
  //
  //   // No need to refresh data after token generation
  //   // Token generation doesn't affect the quick link list
  // }

  /**
   * Delete quick link
   */
  private deleteQuickLink(item: QuickLinkListItem): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const accessToken = this.getAccessToken();
        if (!accessToken) return;

        this.quickLinksService
          .deleteQuickLink(this.currentSiteId()!, item.id, accessToken)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Quick link deleted successfully",
              });
              this.refreshData();
            },
            error: (error) => {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to delete quick link",
                life: 5000,
              });
            },
          });
      },
    });
  }

  /**
   * Permanently delete quick link (for already deleted items)
   * This action cannot be undone
   */
  private permanentDeleteQuickLink(item: QuickLinkListItem): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to permanently delete "<strong>${item.name}</strong>"? This action cannot be undone. All metrics, consumed links, and tracking data will be permanently lost.`,
      header: "Permanent Delete",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Yes, Delete Permanently",
      rejectLabel: "Cancel",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        const accessToken = this.getAccessToken();
        if (!accessToken) return;

        this.quickLinksService
          .permanentDeleteQuickLink(this.currentSiteId()!, item.id, accessToken)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "The quick link has been permanently deleted",
              });
              this.refreshData();
            },
            error: (error) => {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to permanently delete quick link",
                life: 5000,
              });
            },
          });
      },
    });
  }

  /**
   * Toggle quick link status
   * Status: 0=Disabled, 1=Enabled, 2=Deleted
   * The item parameter contains the new status from the toggle switch
   */
  private toggleStatus(item: QuickLinkListItem): void {
    const newStatus: QuickLinkStatus = item.status; // Use the status from the toggle switch
    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    this.quickLinksService
      .updateQuickLinkStatus(
        this.currentSiteId()!,
        item.id,
        { status: newStatus },
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Quick link ${newStatus === 1 ? "enabled" : "disabled"}`,
          });
          this.refreshData();
        },
        error: (error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to update status",
            life: 5000,
          });
          // Refresh data to revert UI to actual state
          this.refreshData();
        },
      });
  }

  /**
   * Toggle one-time use setting
   * UsageLimitType: 0=SingleUse, 1=LimitedUse, 2=Unlimited
   * The item parameter already contains all current data from the table row,
   * including the new usageLimitType and maxUses from the checkbox
   */
  private toggleOneTimeUse(item: QuickLinkListItem): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    const newUsageLimitType = item.usageLimitType;
    const newMaxUses = item.maxUses;

    // Build UpdateQuickLinkRequest using data from table row
    // Only usageLimitType and maxUses are updated values, rest is current data
    // IMPORTANT: API requires ALL redirect fields to be present (even if null)
    // Empty strings must be converted to null
    //
    // CRITICAL: maxUses validation per Postman collection (line 163):
    // - SingleUse (0): maxUses MUST be 1
    // - LimitedUse (1): maxUses MUST be a number (e.g., 100, 200)
    // - Unlimited (2): maxUses MUST be null
    let correctMaxUses: number | null;
    if (newUsageLimitType === 0) {
      // SingleUse
      correctMaxUses = 1;
    } else if (newUsageLimitType === 2) {
      // Unlimited
      correctMaxUses = null;
    } else {
      // LimitedUse
      correctMaxUses = newMaxUses;
    }

    const updateRequest = {
      name: item.name,
      actionType: item.actionType,
      usageLimitType: newUsageLimitType,
      maxUses: correctMaxUses,
      requiresLogin: item.requiresLogin,
      requiresConfirmation: item.requiresConfirmation,
      expiredUtc: item.expiredUtc || null, // Empty string → null
      redirect: {
        type: item.redirect?.type ?? 0,
        message: item.redirect?.message || null, // Empty string → null
        customUrl: item.redirect?.customUrl || null, // Empty string → null
        utmParams: item.redirect?.utmParams || null, // Empty string → null
      },
    };

    this.quickLinksService
      .updateQuickLink(
        this.currentSiteId()!,
        item.id,
        updateRequest,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Quick link updated to ${newUsageLimitType === 2 ? "single-use" : "limited use (100 max uses)"}`,
          });
          this.refreshData();
        },
        error: (error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to update one-time use setting",
            life: 5000,
          });
          // Refresh data to revert UI to actual state
          this.refreshData();
        },
      });
  }

  /**
   * Refresh data by re-triggering form changes
   */
  private refreshData(): void {
    this.filterForm.markAsDirty();
    this.filterForm.updateValueAndValidity();
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(): void {
    const newState = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(newState);
    this.userManuallyCollapsed = newState; // Track user preference
  }

  /**
   * Initialize sidebar state based on screen size
   */
  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      // Only auto-collapse on mobile if user hasn't manually set preference
      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        // Only auto-expand on desktop if user hasn't manually collapsed
        this.sidebarCollapsed.set(false);
      }

      this.cdr.markForCheck();
    });

    // Observe the window for resize changes
    resizeObserver.observe(document.body);

    // Clean up observer on destroy
    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }
}
